import { AfterViewInit, Component, ElementRef, OnInit, Renderer2, ViewChild } from '@angular/core'
import { Title } from '@angular/platform-browser'
import { ActivatedRoute, Params } from '@angular/router'
import { IonContent, IonRouterOutlet } from '@ionic/angular'
import { Subscription } from 'rxjs'
import { Page } from 'src/app/core/page'
import { LogEvent } from 'src/app/interfaces/log'
import { MBTI } from 'src/app/interfaces/mbti'
import { AskService } from 'src/app/services/ask/ask.service'
import { GeneralService } from 'src/app/services/general/general.service'
import { LocaleService } from 'src/app/services/locale/locale-service'
import { LogService } from 'src/app/services/log/log.service'
import { MbtiService } from 'src/app/services/mbti/mbti.service'
import { ModalService } from 'src/app/services/modal/modal.service'
import { RouterService } from 'src/app/services/router/router.service'
import { SurveyService } from 'src/app/services/survey/survey.service'
import { UserService } from 'src/app/services/user/user.service'
import { environment } from 'src/environments/environment'
import { customLocalStorage } from 'src/utils/utils'
import { FeedCard } from './../../interfaces/main'
import { PermissionStatusRecorded, PermissionsType } from './../../interfaces/permission'
import { AskMainComponent } from './ask-main/ask-main.component'
import { Feed, feedPaths } from './feed'
import { SurveyMainComponent } from './survey-main/survey-main.component'

@Component({
  selector: 'app-main',
  templateUrl: './main.component.html',
  styleUrls: ['./main.component.scss'],
})
export class MainComponent extends Page implements OnInit, AfterViewInit {
  @ViewChild('askMain') askMainComponent: AskMainComponent | undefined
  @ViewChild('surveyMain') surveyMainComponent: SurveyMainComponent | undefined

  ionContent: IonContent | undefined
  screenName = 'home'
  isContentLoading = true
  isRefreshing = false
  cardList: (FeedCard & { isExposed?: boolean })[] = []
  imageLoaded: boolean[] = []
  scrollTop = 0
  alreadyLoggedCardIndex: number[] = []
  isAskVisible = this.askService.isAskVisibleOnHome
  isSurveyVisible = this.surveyService.isSurveyVisibleOnHome
  feedList: Feed[] = []
  protected blockBackButton: boolean = true
  private isAskVisibleSubscription: Subscription | undefined
  private isSurveyVisibleSubscription: Subscription | undefined
  private queryParamSubscription: Subscription | undefined
  private expValueChangedSubscription: Subscription | undefined

  get myMbti(): MBTI | null | undefined {
    return this.mbtiService.myMbti
  }

  constructor(
    public generalService: GeneralService,
    protected routerService: RouterService,
    protected routerOutlet: IonRouterOutlet,
    protected modalService: ModalService,
    protected title: Title,
    protected logService: LogService,
    protected localeService: LocaleService,
    private userService: UserService,
    private elementRef: ElementRef,
    private renderer: Renderer2,
    private askService: AskService,
    private surveyService: SurveyService,
    private mbtiService: MbtiService,
    private activatedRoute: ActivatedRoute
  ) {
    super(generalService, routerService, routerOutlet, modalService, title, logService, localeService)
    this.getCardList()
  }

  async ngOnInit(): Promise<void> {
    // 임시 제거
    // this.showCoupangModal()
    if (this.queryParamSubscription === undefined) {
      this.queryParamSubscription = this.activatedRoute.queryParams.subscribe((params: Params) => {
        if (params.scrollToSurvey) {
          const scrollTimeout = setTimeout(() => {
            const surveyElem: HTMLElement = this.elementRef.nativeElement.querySelector('survey-main')
            if (surveyElem) {
              surveyElem.scrollIntoView({ behavior: 'smooth' })
            }
            clearTimeout(scrollTimeout)
          }, 500)
        }
      })
    }
    if (this.isAskVisibleSubscription === undefined) {
      this.isAskVisibleSubscription = this.askService.getIsAskVisibleOnHomeSubject().subscribe((value) => {
        this.isAskVisible = this.askService.isAskVisibleOnHome
      })
    }
    if (this.isSurveyVisibleSubscription === undefined) {
      this.isSurveyVisibleSubscription = this.surveyService.getIsSurveyVisibleOnHomeSubject().subscribe((value) => {
        this.isSurveyVisible = this.surveyService.isSurveyVisibleOnHome
      })
    }
    if (this.expValueChangedSubscription === undefined) {
      this.expValueChangedSubscription = this.logService.exp.getExpValueChangedSubject().subscribe((data) => {
        let needSetFeedList = false
        data.forEach((item) => {
          if (item.isRelatedToFeed) {
            needSetFeedList = true
          }
        })
        if (needSetFeedList) {
          this.setFeedList()
        }
      })
    }

    this.logService.eventLogging(LogEvent.HOME_VIEWED, { screenName: 'home' })
    this.setTitle('common.pageTitle')
    this.getUserMbti()
    const logImpressionTimeout = setTimeout(() => {
      this.logImpression()
      clearTimeout(logImpressionTimeout)
    }, 500)
  }

  ngAfterViewInit(): void {
    const loadingTimeout = setTimeout(() => {
      this.isContentLoading = false
      clearTimeout(loadingTimeout)
    })
  }

  ngOnDestroy() {
    super.ngOnDestroy()
    if (this.isAskVisibleSubscription !== undefined) {
      this.isAskVisibleSubscription.unsubscribe()
    }
    if (this.isSurveyVisibleSubscription !== undefined) {
      this.isSurveyVisibleSubscription.unsubscribe()
    }
    if (this.queryParamSubscription !== undefined) {
      this.queryParamSubscription.unsubscribe()
    }
  }

  doRefresh(event): void {
    this.isRefreshing = true
    this.ngOnDestroy()
    this.ngOnInit()
    this.askMainComponent?.doRefresh(event)
    this.surveyMainComponent?.doRefresh(event)
    const completeTimeout = setTimeout(() => {
      event.target.complete()
      this.isRefreshing = false
      clearTimeout(completeTimeout)
    }, 2000)
  }

  isPermissionAskedAfterLogin(): boolean {
    return customLocalStorage.getItem(PermissionsType.PUSH_AFTER_LOGIN) === PermissionStatusRecorded.YES
  }

  onClickCard(card: FeedCard): void {
    if (card.moveTo.includes(environment.externalLink.base)) {
      this.generalService.openInappBrowser(card.moveTo)
      return
    }
    let { moveTo } = card
    moveTo = moveTo.startsWith('/') ? moveTo : `/${moveTo}`
    const url = new URL(`${environment.webUrl}${moveTo}`)
    let queryParams: object | undefined
    if (url.search && url.search !== '') {
      for (const [key, value] of (url.searchParams as any).entries()) {
        if (queryParams === undefined) {
          queryParams = {
            [key]: value,
          }
        } else {
          queryParams[key] = value
        }
      }
    }
    const logParams = {
      title: card.title,
      path: url.pathname,
    }
    this.logService.eventLogging(LogEvent.HOME_FEED_CLICK, { ...logParams })
    this.routerService.navigate(url.pathname, { queryParams })
  }

  onScrollEvent(event): void {
    this.ionContent?.getScrollElement().then((res) => {
      this.scrollTop = res.scrollTop
    })
    const cardElemList: HTMLElement[] = this.elementRef.nativeElement.querySelectorAll('.card')
    const windowHeight = window.innerHeight
    cardElemList.forEach((cardElem, index) => {
      const top = cardElem.getBoundingClientRect().top
      const card = this.cardList[index]
      if (card && !card.isExposed && (top / windowHeight) * 100 < 65) {
        card.isExposed = true
        this.logService.mainFeedExposeLogging(card, index)
      }
    })
  }

  private getUserMbti(): void {
    if (this.userService.isLoggedIn) {
      this.mbtiService.getMyMbtiHostQuizReportList(null).subscribe()
    }
  }

  private logImpression(): void {
    this.askImpressionLogging()
    this.surveyImpressionLogging()
    this.signalImpressionLogging()
    this.thinkingChairImpressionLogging()
    this.attachmentQuizImpressionLogging()
    this.imageGameImpressionLogging()
    this.guessMeImpressionLogging()
  }

  private observeImpression(className: string, callback: () => void): void {
    const targetElem: HTMLElement = this.elementRef.nativeElement.querySelector(className)
    if (targetElem) {
      const options = {
        root: this.elementRef.nativeElement.querySelector('ion-content'),
        rootMargin: '0px',
        threshold: 0.1,
      }
      const intersectionObserver = new IntersectionObserver((res: [IntersectionObserverEntry]) => {
        const { isIntersecting } = res[0]
        if (isIntersecting) {
          callback()
          intersectionObserver.unobserve(targetElem)
        }
      }, options)
      intersectionObserver.observe(targetElem)
    }
  }

  private thinkingChairImpressionLogging(): void {
    this.observeImpression('.feed_tc', () => this.logService.tc.homeThinkingChairImpression())
  }

  private signalImpressionLogging(): void {
    this.observeImpression('.feed_signal', () => this.logService.signal.homeSignalImpression())
  }

  /** 최초 한번만 impression log */
  private askImpressionLogging(): void {
    this.observeImpression('.ask-main-container', () => this.askMainComponent?.homeAskImpressionLogging())
  }

  /** 최초 한번만 impression log */
  private surveyImpressionLogging(): void {
    this.observeImpression('.survey-main-container', () => this.surveyMainComponent?.homeSurveyImpressionLogging())
  }

  /** 최초 한번만 impression log */
  private attachmentQuizImpressionLogging(): void {
    this.observeImpression('.feed_attachment-quiz', () => this.logService.attachment.logHomeAttachmentQuizImpression())
  }

  private imageGameImpressionLogging(): void {
    this.observeImpression('.feed_image-game', () => this.logService.imageGame.logHomeImageGameImpression())
  }

  private guessMeImpressionLogging(): void {
    this.observeImpression('.feed_guess-me', () => this.logService.guessMe.logHomeGuessMeImpression())
  }

  private getCardList(): void {
    this.generalService.getFeedCardList().subscribe((res) => {
      this.cardList = (res.data ?? []).filter((item) => !item.type || item.type === 'banner')
      this.setFeedList()
      const setHeightTimeout = setTimeout(() => {
        this.setDefaultCardHeight()
        clearTimeout(setHeightTimeout)
      })
    })
  }

  private async setFeedList(): Promise<void> {
    const paths = feedPaths[this.localeService.locale]
    this.feedList = paths.reduce((acc, { type, pathname, needToInstallApp }) => {
      // survey, ask만 따로 처리
      if (type !== 'feed-card') {
        acc.push({ type, needToInstallApp })
        return acc
      }
      const card = this.cardList.find((item) => item.moveTo === pathname)
      if (card) {
        acc.push({ type, data: card, needToInstallApp })
      }
      return acc
    }, [] as Feed[])

    const scrollTimeout = setTimeout(() => {
      // impression logging 동작하려면 스크롤이 되어야하므로 1px만큼만 스크롤 해주기
      this.ionContent?.scrollToPoint(0, 1)
      clearTimeout(scrollTimeout)
    }, 1000)
  }

  /** 16:9의 이미지 비율대로 높이 설정 */
  private setDefaultCardHeight(): void {
    const containerElem = this.elementRef.nativeElement.querySelector('.main-content-container')
    const imgWraperElems = [...this.elementRef.nativeElement.querySelectorAll('.img-wrapper')]
    if (containerElem && Array.isArray(imgWraperElems)) {
      imgWraperElems.forEach((elem) => {
        const paddingLeft = +window
          .getComputedStyle(containerElem, null)
          .getPropertyValue('padding-left')
          .replace('px', '')
        const paddingRight = +window
          .getComputedStyle(containerElem, null)
          .getPropertyValue('padding-right')
          .replace('px', '')
        const width = Math.min(500, window.innerWidth) - (paddingLeft + paddingRight)
        this.renderer.setStyle(elem, 'height', width * (9 / 16) + 'px')
      })
    }
  }
}
